"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.priceModelLabelFor = exports.buildManualPriceModelValues = exports.buildPlanPriceModelValues = exports.resolveEnergyRate = exports.getConfiguredVariantId = exports.getConfiguredRatePlan = exports.canAutoUpdateRate = exports.getEnergyRateSource = exports.getRatePerKwh = exports.DEFAULT_RATE_PER_KWH = void 0;
const ratePlans_1 = require("./energy/ratePlans");
const tariffEngine_1 = require("./energy/tariffEngine");
/**
 * The single place a kWh rate is resolved. Every cost path — common hooks, web-hub, web-admin,
 * mobile — must read the rate through here and never hardcode one.
 *
 * ## The ladder
 *
 * 1. **manual** — someone typed a rate, normally off a real bill. Always wins.
 * 2. **plan** — blended from the household's selected utility rate plan.
 * 3. **default** — the fleet fallback below.
 *
 * Note what is deliberately absent: any network call. Rate plans are committed tables, so a
 * quoted rate is reproducible and traceable to a dated source rather than depending on a
 * third-party API being reachable at install time.
 */
/**
 * Fallback when nothing better is known.
 *
 * Set to SCE's over-baseline rate rather than a state or national average, because the fleet is
 * overwhelmingly in SCE territory and California's ~$0.31 average is *below SCE's cheapest tier* —
 * using it would understate every Edison customer's cost. It is a deliberate overestimate: a
 * household under its baseline allowance pays nearer $0.336, so this errs toward over-quoting
 * rather than promising savings that will not appear on the bill.
 *
 * Must match the value seeded into hub config by the infrastructure (`sensorStreamHandler` and
 * `hubResolver`) so a hub whose config has not synced yet computes the same costs it will after.
 */
exports.DEFAULT_RATE_PER_KWH = 0.407;
/**
 * Rate written into hub config before provenance was tracked.
 *
 * Legacy configs carry a bare `rate_per_kWh` with no `rateSource`, so whether a person chose it
 * or a seed wrote it is unrecoverable. Exactly this value is treated as a seed; anything else is
 * treated as a deliberate human edit and protected from automatic replacement. The asymmetry is
 * intentional — wrongly protecting a seeded value costs an automatic refresh, whereas wrongly
 * overwriting a rate someone read off their bill destroys the best data available.
 */
const LEGACY_SEEDED_RATE = 0.407;
const priceModelValues = (hubConfig) => hubConfig?.Electrical?.PriceModelValues;
const configuredRate = (hubConfig) => {
    const rate = priceModelValues(hubConfig)?.rate_per_kWh;
    return typeof rate === 'number' && rate > 0 ? rate : undefined;
};
/**
 * The rate to bill at, in $/kWh.
 *
 * Signature and behaviour are unchanged from before provenance existed: a configured positive
 * rate wins, otherwise the default. Existing callers need no changes.
 */
const getRatePerKwh = (hubConfig) => configuredRate(hubConfig) ?? exports.DEFAULT_RATE_PER_KWH;
exports.getRatePerKwh = getRatePerKwh;
/**
 * Provenance of the configured rate, inferring it for hubs predating the field.
 *
 * A legacy rate that differs from the seeded value is reported as `manual`, on the reasoning in
 * {@link LEGACY_SEEDED_RATE}.
 */
const getEnergyRateSource = (hubConfig) => {
    const values = priceModelValues(hubConfig);
    if (values?.rateSource)
        return values.rateSource;
    const rate = configuredRate(hubConfig);
    if (rate === undefined)
        return 'default';
    return rate === LEGACY_SEEDED_RATE ? 'default' : 'manual';
};
exports.getEnergyRateSource = getEnergyRateSource;
/**
 * Whether an automatic lookup may overwrite this hub's rate.
 *
 * The guard behind "never overwrite what a person typed". Callers that resolve rates from a zip
 * or refresh a plan table must check this first.
 */
const canAutoUpdateRate = (hubConfig) => (0, exports.getEnergyRateSource)(hubConfig) !== 'manual';
exports.canAutoUpdateRate = canAutoUpdateRate;
/** The rate plan this hub is on, if one was selected and still exists in the tables. */
const getConfiguredRatePlan = (hubConfig) => {
    const planId = priceModelValues(hubConfig)?.planId;
    return planId ? (0, ratePlans_1.getRatePlan)(planId) : undefined;
};
exports.getConfiguredRatePlan = getConfiguredRatePlan;
/**
 * Territory variant to price this hub under — its selection, or the plan's default.
 *
 * SCE assigns baseline regions by community rather than by zip, so this cannot be derived from a
 * hub's address and is either chosen or defaulted. The cost of defaulting is modest: regions
 * differ only in tier allowance, not in rate, which spans about 6% of blended cost at a typical
 * heavy-usage month.
 */
const getConfiguredVariantId = (hubConfig) => {
    const values = priceModelValues(hubConfig);
    const plan = (0, exports.getConfiguredRatePlan)(hubConfig);
    if (!plan)
        return undefined;
    const requested = values?.variantId;
    return requested && plan.periodsByVariant[requested] ? requested : plan.defaultVariantId;
};
exports.getConfiguredVariantId = getConfiguredVariantId;
/**
 * Full picture of a hub's rate: the number, where it came from, and the plan behind it.
 *
 * Use this over {@link getRatePerKwh} when a surface can do more with a tariff than multiply —
 * time-of-use aware charts, or explaining to a homeowner why their rate is what it is.
 */
const resolveEnergyRate = (hubConfig) => {
    const source = (0, exports.getEnergyRateSource)(hubConfig);
    const plan = (0, exports.getConfiguredRatePlan)(hubConfig);
    const variantId = (0, exports.getConfiguredVariantId)(hubConfig);
    const utilityId = priceModelValues(hubConfig)?.utilityId ?? plan?.utilityId;
    return {
        ratePerKwh: (0, exports.getRatePerKwh)(hubConfig),
        source,
        plan,
        variantId,
        utilityName: utilityId ? (0, ratePlans_1.getUtility)(utilityId)?.name : undefined,
    };
};
exports.resolveEnergyRate = resolveEnergyRate;
/**
 * Builds the `PriceModelValues` for a plan selection, blending the plan to a single rate so
 * tariff-unaware surfaces keep working.
 *
 * `monthlyKwh` should be the household's own average once there is enough history; until then the
 * blend rests on {@link DEFAULT_MONTHLY_KWH}, which only affects tiered plans.
 */
const buildPlanPriceModelValues = (planId, options = {}) => {
    const plan = (0, ratePlans_1.getRatePlan)(planId);
    if (!plan)
        return undefined;
    const variantId = options.variantId && plan.periodsByVariant[options.variantId]
        ? options.variantId
        : plan.defaultVariantId;
    const rate = (0, tariffEngine_1.computeBlendedRate)(plan, {
        variantId,
        monthlyKwh: options.monthlyKwh ?? tariffEngine_1.DEFAULT_MONTHLY_KWH,
    });
    return {
        rate_per_kWh: Number(rate.toFixed(5)),
        rateSource: 'plan',
        utilityId: plan.utilityId,
        planId: plan.id,
        // Recorded only when it distinguishes anything, keeping single-territory plans clean.
        ...(Object.keys(plan.periodsByVariant).length > 1 ? { variantId } : {}),
        rateResolvedAt: (options.now ?? new Date()).toISOString(),
    };
};
exports.buildPlanPriceModelValues = buildPlanPriceModelValues;
/** Builds the `PriceModelValues` for a hand-entered rate, marking it protected from refresh. */
const buildManualPriceModelValues = (ratePerKwh) => ({
    rate_per_kWh: ratePerKwh,
    rateSource: 'manual',
});
exports.buildManualPriceModelValues = buildManualPriceModelValues;
/**
 * `PriceModel` label matching a plan's pricing shape, for the existing config field.
 *
 * Kept descriptive rather than load-bearing: nothing dispatches on it, and the plan id is the
 * real key. It exists so someone reading raw hub config can tell what they are looking at.
 */
const priceModelLabelFor = (plan) => {
    if (!plan)
        return 'Fixed';
    const isTou = plan.weekdayGrid.some((row) => new Set(row.split('')).size > 1);
    if (plan.tiered && isTou)
        return 'TieredTOU';
    if (plan.tiered)
        return 'Tiered';
    return isTou ? 'TOU' : 'Fixed';
};
exports.priceModelLabelFor = priceModelLabelFor;
